import { CryptoRandomProvider, type RandomProvider } from '@ai-swfactory/seams/interfaces'
import { FunctionsHttpError, type PostgrestError, type SupabaseClient } from '@supabase/supabase-js'

import { OPERATION_KINDS, PRODUCT_CLASSES, type StayOperation } from '../domain/scales'
import type { Database, Json } from '../schema/database.types'
import type {
  AisStayCheck,
  ConnectionPoint,
  EventRow,
  ScaleRow,
  ScalesSource,
  ScaleWithEventRows,
  SourceRun,
  SourceRunSource,
  SpecialEventInput,
  SyncStart,
} from './types'
import { SourceError } from './types'

type Tables = Database['public']['Tables']
type ScaleDbRow = Tables['scales']['Row']
type EventDbRow = Tables['scale_events']['Row']
type SourceRunDbRow = Tables['source_runs']['Row']
type CheckDbRow = Tables['berth_stay_checks']['Row']

const SOURCE_RUN_COLUMNS = 'id, source, trigger, status, requested_at, started_at, finished_at, summary'

const READ_FUNCTIONS: Record<SourceRunSource, string> = { SOSTRAT: 'sostrat-sync', AIS: 'ais-berth-check' }

const SCALE_COLUMNS =
  'year, scale_id, is_manual, seen_in_source, finished, vessel, imo, eslora, bcm, consignataria, eta, etd, first_eta, first_etd'
const EVENT_COLUMNS =
  'id, year, scale_id, type, muelle, start_time, end_time, terminals, fondeo_previo, anterior, muelles_alternativos, sostrat_terminal, sostrat_term_add, sostrat_state, actual_start_time, actual_end_time, actuals_state, operations, connection_point, product_class, product, quantity_t, operation_kind, marpol_annex, marpol_notice_at'
const CHECK_COLUMNS =
  'berth_stay_checks (status, checked_at, arrival, departure, arrival_difference_s, departure_difference_s, muelle, muelle_agrees, stay_role, arrival_observed)'
const PAGE_SIZE = 1000

type ScaleColumns =
  | 'year'
  | 'scale_id'
  | 'is_manual'
  | 'seen_in_source'
  | 'finished'
  | 'vessel'
  | 'imo'
  | 'eslora'
  | 'bcm'
  | 'consignataria'
  | 'eta'
  | 'etd'
  | 'first_eta'
  | 'first_etd'
type EventColumns =
  | 'id'
  | 'year'
  | 'scale_id'
  | 'type'
  | 'muelle'
  | 'start_time'
  | 'end_time'
  | 'terminals'
  | 'fondeo_previo'
  | 'anterior'
  | 'muelles_alternativos'
  | 'sostrat_terminal'
  | 'sostrat_term_add'
  | 'sostrat_state'
  | 'actual_start_time'
  | 'actual_end_time'
  | 'actuals_state'
  | 'operations'
  | 'connection_point'
  | 'product_class'
  | 'product'
  | 'quantity_t'
  | 'operation_kind'
  | 'marpol_annex'
  | 'marpol_notice_at'

const toScaleRow = (row: Pick<ScaleDbRow, ScaleColumns>): ScaleRow => ({
  year: row.year,
  scaleId: row.scale_id,
  isManual: row.is_manual,
  seenInSource: row.seen_in_source,
  finished: row.finished,
  vessel: row.vessel,
  imo: row.imo,
  eslora: row.eslora,
  bcm: row.bcm,
  consignataria: row.consignataria,
  eta: row.eta,
  etd: row.etd,
  firstEta: row.first_eta,
  firstEtd: row.first_etd,
})

const field = (value: Json | undefined): Json | undefined => value
const text = (value: Json | undefined): string | null => (typeof value === 'string' ? value : null)
const number = (value: Json | undefined): number | null => (typeof value === 'number' ? value : null)
const oneOf = <T extends string>(values: readonly T[], value: Json | undefined): T | null =>
  values.find(candidate => candidate === value) ?? null

const toStayOperations = (json: Json): StayOperation[] =>
  (Array.isArray(json) ? json : []).flatMap(item => {
    if (item === null || typeof item !== 'object' || Array.isArray(item)) return []
    return [
      {
        state: text(field(item.state)),
        operation: text(field(item.operation)),
        kind: oneOf(OPERATION_KINDS, field(item.kind)),
        goods: text(field(item.goods)),
        productClass: oneOf(PRODUCT_CLASSES, field(item.product_class)),
        quantity: number(field(item.quantity)),
        unit: text(field(item.unit)),
        quantityT: number(field(item.quantity_t)),
        quantityBasis: oneOf(['TONNES', 'KG', 'IMPLAUSIBLE'] as const, field(item.quantity_basis)),
        estibador: text(field(item.estibador)),
        terminal: text(field(item.terminal)),
        windowStart: text(field(item.window_start)),
        windowEnd: text(field(item.window_end)),
      },
    ]
  })

type CheckColumns =
  | 'status'
  | 'checked_at'
  | 'arrival'
  | 'departure'
  | 'arrival_difference_s'
  | 'departure_difference_s'
  | 'muelle'
  | 'muelle_agrees'
  | 'stay_role'
  | 'arrival_observed'

type CheckRow = Pick<CheckDbRow, CheckColumns> & { arrival: string }

const latestFirst = (a: CheckRow, b: CheckRow): number => Date.parse(b.checked_at) - Date.parse(a.checked_at)

const ARRIVAL_ROLES: readonly (string | null)[] = ['ONLY', 'FIRST']
const DEPARTURE_ROLES: readonly (string | null)[] = ['ONLY', 'LAST']

export const toAisStayCheck = (rows: readonly Pick<CheckDbRow, CheckColumns>[]): AisStayCheck | null => {
  const matched = rows.filter((row): row is CheckRow => row.status !== 'ONLY_IN_SOSTRAT' && row.arrival !== null).sort(latestFirst)
  const arrivalRow = matched.find(row => ARRIVAL_ROLES.includes(row.stay_role))
  if (!arrivalRow) {
    const [check] = matched.filter(row => row.stay_role === null)
    if (!check) return null
    return {
      arrival: check.arrival,
      departure: check.departure,
      arrivalDifferenceS: check.arrival_difference_s,
      departureDifferenceS: check.departure_difference_s,
      arrivalObserved: check.arrival_observed,
      muelle: check.muelle,
      muelleAgrees: check.muelle_agrees,
    }
  }
  const departureRow = matched.find(row => DEPARTURE_ROLES.includes(row.stay_role))
  const roleRows = matched.filter(row => row.stay_role !== null)
  const disagreeing = roleRows.find(row => row.muelle_agrees === false)
  return {
    arrival: arrivalRow.arrival,
    departure: departureRow?.departure ?? null,
    arrivalDifferenceS: arrivalRow.arrival_difference_s,
    departureDifferenceS: departureRow?.departure_difference_s ?? null,
    arrivalObserved: arrivalRow.arrival_observed,
    muelle: (disagreeing ?? arrivalRow).muelle,
    muelleAgrees: disagreeing ? false : arrivalRow.muelle_agrees,
  }
}

type EventWithChecksDb = Pick<EventDbRow, EventColumns> & { berth_stay_checks?: Pick<CheckDbRow, CheckColumns>[] }

const toEventRow = (row: EventWithChecksDb): EventRow => {
  const base = {
    id: row.id,
    year: row.year,
    scaleId: row.scale_id,
    startTime: row.start_time,
    endTime: row.end_time,
    terminals: row.terminals,
    fondeoPrevio: row.fondeo_previo,
    anterior: row.anterior,
    muellesAlternativos: row.muelles_alternativos,
    sostratTerminal: row.sostrat_terminal,
    sostratTermAdd: row.sostrat_term_add,
    sostratState: row.sostrat_state,
    operations: toStayOperations(row.operations),
    ais: toAisStayCheck(row.berth_stay_checks ?? []),
    actualStartTime: row.actual_start_time,
    actualEndTime: row.actual_end_time,
    actualsState: row.actuals_state,
    connectionPoint: row.connection_point,
    productClass: row.product_class,
    product: row.product,
    quantityT: row.quantity_t,
    operationKind: row.operation_kind,
    marpolAnnex: row.marpol_annex,
    marpolNoticeAt: row.marpol_notice_at,
  }
  if (row.type !== 'PLACE') return { ...base, type: row.type }
  if (row.muelle === null) throw new Error(`PLACE event ${row.id} has no muelle`)
  return { ...base, type: 'PLACE', muelle: row.muelle }
}

const isObject = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value)

const countsOf = (summary: Record<string, unknown>, prefix = ''): [string, number][] =>
  Object.entries(summary).flatMap(([name, value]): [string, number][] => {
    if (typeof value === 'number') return [[`${prefix}${name}`, value]]
    return isObject(value) ? countsOf(value, `${prefix}${name}.`) : []
  })

export const toSourceRun = (
  row: Pick<SourceRunDbRow, 'id' | 'source' | 'trigger' | 'status' | 'requested_at' | 'started_at' | 'finished_at' | 'summary'>,
): SourceRun => {
  const summary = isObject(row.summary) ? row.summary : {}
  const problems = Array.isArray(summary.problems) ? summary.problems.map(String) : []
  return {
    id: row.id,
    source: row.source,
    trigger: row.trigger,
    status: row.status,
    requestedAt: row.requested_at,
    startedAt: row.started_at,
    finishedAt: row.finished_at,
    counts: Object.fromEntries(countsOf(summary)),
    problems,
  }
}

const toSyncStart = (body: unknown): SyncStart | null => {
  if (!isObject(body) || !isObject(body.run)) return null
  const { id, requestedAt } = body.run
  if (typeof id !== 'string' || typeof requestedAt !== 'string') return null
  if (body.kind === 'started' || body.kind === 'busy') return { kind: body.kind, run: { id, requestedAt } }
  return null
}

const toSourceError = (error: PostgrestError): Error => {
  if (error.message.includes('end_not_before_start')) return new SourceError('END_BEFORE_START', error.message)
  if (error.code === 'PGRST116') return new SourceError('NOT_FOUND', error.message)
  return new Error(error.message)
}

const unwrap = <T>({ data, error }: { data: T; error: PostgrestError | null }): NonNullable<T> => {
  if (error) throw toSourceError(error)
  if (data === null || data === undefined) throw new SourceError('NOT_FOUND')
  return data
}

type ScaleWithEventsDb = Pick<ScaleDbRow, ScaleColumns> & {
  scale_events: EventWithChecksDb[]
}

const toScaleWithEvents = (row: ScaleWithEventsDb): ScaleWithEventRows => ({
  scale: toScaleRow(row),
  events: row.scale_events.map(toEventRow),
})

const specialEventColumns = (input: SpecialEventInput) => {
  const bombeo = input.type === 'BOMBEO'
  const marpol = input.type === 'MARPOL'
  return {
    type: input.type,
    start_time: input.startTime,
    end_time: input.endTime,
    connection_point: bombeo ? input.connectionPoint : null,
    product_class: bombeo ? input.productClass : null,
    product: bombeo ? input.product : null,
    quantity_t: bombeo ? input.quantityT : null,
    operation_kind: bombeo ? input.operationKind : null,
    marpol_annex: marpol ? input.marpolAnnex : null,
    marpol_notice_at: marpol ? input.marpolNoticeAt : null,
  }
}

export const createSupabaseSource = (
  getClient: () => SupabaseClient<Database>,
  random: RandomProvider = new CryptoRandomProvider(),
): ScalesSource => {
  const scalesWithEvents = () => getClient().from('scales').select(`${SCALE_COLUMNS}, scale_events (${EVENT_COLUMNS}, ${CHECK_COLUMNS})`)

  return {
    listScales: async ({ finished }) => {
      const all: ScaleWithEventRows[] = []
      for (let from = 0; ; from += PAGE_SIZE) {
        let query = scalesWithEvents()
        if (finished !== undefined) query = query.eq('finished', finished)
        const page = unwrap(
          await query
            .order('year')
            .order('scale_id')
            .range(from, from + PAGE_SIZE - 1),
        )
        all.push(...page.map(toScaleWithEvents))
        if (page.length < PAGE_SIZE) return all
      }
    },

    getScale: async ({ year, scaleId }) => {
      const result = await scalesWithEvents().eq('year', year).eq('scale_id', scaleId).maybeSingle()
      if (result.error) throw toSourceError(result.error)
      return result.data ? toScaleWithEvents(result.data) : undefined
    },

    getEvent: async id => {
      const result = await getClient().from('scale_events').select(EVENT_COLUMNS).eq('id', id).maybeSingle()
      if (result.error) throw toSourceError(result.error)
      return result.data ? toEventRow(result.data) : undefined
    },

    createSpecialEvent: async ({ year, scaleId }, input: SpecialEventInput) => {
      const row = unwrap(
        await getClient()
          .from('scale_events')
          .insert({ id: random.generateUUID(), year, scale_id: scaleId, ...specialEventColumns(input) })
          .select(EVENT_COLUMNS)
          .single(),
      )
      return { ...toEventRow(row), type: input.type }
    },

    updateSpecialEvent: async (id, input: SpecialEventInput) => {
      const existing = unwrap(await getClient().from('scale_events').select('type').eq('id', id).maybeSingle())
      if (existing.type !== input.type) throw new SourceError('NOT_SPECIAL')
      const row = unwrap(
        await getClient().from('scale_events').update(specialEventColumns(input)).eq('id', id).select(EVENT_COLUMNS).single(),
      )
      return { ...toEventRow(row), type: input.type }
    },

    setFinished: async ({ year, scaleId }, finished) =>
      toScaleRow(
        unwrap(
          await getClient().from('scales').update({ finished }).eq('year', year).eq('scale_id', scaleId).select(SCALE_COLUMNS).single(),
        ),
      ),

    listConnectionPoints: async () =>
      unwrap(await getClient().from('connection_points').select('code, muelle, terminal, status, note').order('code')).map(
        (row): ConnectionPoint => ({ code: row.code, muelle: row.muelle, terminal: row.terminal, status: row.status, note: row.note }),
      ),

    startRead: async source => {
      const name = READ_FUNCTIONS[source]
      const result = await getClient().functions.invoke<unknown>(name, { method: 'POST' })
      const error: unknown = result.error
      if (error === null) {
        const started = toSyncStart(result.data)
        if (started) return started
        throw new Error(`${name} answered ${JSON.stringify(result.data)}`)
      }
      if (!(error instanceof FunctionsHttpError)) throw error instanceof Error ? error : new Error(JSON.stringify(error))
      const response: unknown = error.context
      if (!(response instanceof Response)) throw error
      const body: unknown = await response.json().catch(() => null)
      if (response.status === 409) {
        const busy = toSyncStart(body)
        if (busy) return busy
      }
      const message = isObject(body) && typeof body.message === 'string' ? body.message : `HTTP ${String(response.status)}`
      if (response.status === 502 || source === 'AIS') throw new SourceError('SYNC_REFUSED', message)
      throw new Error(message)
    },

    latestRun: async source => {
      const result = await getClient()
        .from('source_runs')
        .select(SOURCE_RUN_COLUMNS)
        .eq('source', source)
        .order('requested_at', { ascending: false })
        .limit(1)
        .maybeSingle()
      if (result.error) throw toSourceError(result.error)
      return result.data ? toSourceRun(result.data) : undefined
    },

    getSourceRun: async id => {
      const result = await getClient().from('source_runs').select(SOURCE_RUN_COLUMNS).eq('id', id).maybeSingle()
      if (result.error) throw toSourceError(result.error)
      return result.data ? toSourceRun(result.data) : undefined
    },
  }
}
